export type CatalogCategory = {
  id: number;
  name: string;
  description: string | null;
  productCount: number;
};

export type CatalogProduct = {
  id: number;
  slug: string;
  name: string;
  price: number;
  quantity: number;
  category: string;
  description: string;
  testedWith: string | null;
  image: string | null;
  addedOn: string | null;
  totalPurchase: number;
  inStock: boolean;
};

export type CatalogResponse = {
  filters: {
    query?: string | null;
    categoryId?: number | null;
    category?: string | null;
    minPrice?: number | null;
    maxPrice?: number | null;
    inStock?: boolean;
    sort?: "featured" | "price_asc" | "price_desc" | "newest" | "popular";
    limit?: number;
  };
  products: CatalogProduct[];
  categories: CatalogCategory[];
  meta: {
    totalProducts: number;
    minPrice: number;
    maxPrice: number;
  };
};

export type SearchResponse = {
  query: string;
  results: Array<{
    id: number;
    slug: string;
    name: string;
    sku: string | null;
    category: string | null;
    price: number;
    quantity: number;
    image: string | null;
    description: string | null;
    score: number;
  }>;
};

export type ProductDetailResponse = {
  product: {
    id: number;
    slug: string;
    name: string;
    price: number;
    minimumPrice: number | null;
    quantity: number;
    description: string;
    testedWith: string | null;
    codeUrl: string | null;
    sku: string | null;
    brand: string | null;
    specSummary: string | null;
    keywords: string | null;
    totalPurchase: number;
    addedOn: string | null;
    categoryId: number | null;
    category: string;
    inStock: boolean;
    primaryImage: string | null;
    gallery: Array<{
      id: number;
      url: string | null;
      altText: string;
    }>;
  };
  specs: string[];
  related: CatalogProduct[];
};

export type OrderInvoiceResponse = {
  order: {
    id: number;
    total: number;
    subtotal: number;
    discount: number;
    amountPaid: number;
    balance: number;
    status: string;
    addedOn: string | null;
    trackingNumber: string | null;
    deliveryName: string;
    deliveryPhone: string;
    deliveryAddress: string;
    email: string | null;
    deliveryNote: string | null;
  };
  items: Array<{
    id: number;
    quantity: number;
    unitPrice: number;
    total: number;
    productId: number | null;
    name: string;
    slug: string | null;
  }>;
};

export function productPath(slug: string, id: number) {
  return `/shop/product/${slug}/${id}`;
}

export function categoryPath(id: number, name: string) {
  return `/shop/categories/${id}/${encodeURIComponent(name.trim())}`;
}

export async function fetchCatalog(query: URLSearchParams) {
  const response = await fetch(`/api/catalog?${query.toString()}`, {
    headers: { Accept: "application/json" }
  });

  if (!response.ok) {
    throw new Error(`Catalog request failed: ${response.status}`);
  }

  return (await response.json()) as CatalogResponse;
}

export async function fetchSearch(query: URLSearchParams) {
  const response = await fetch(`/api/search?${query.toString()}`, {
    headers: { Accept: "application/json" }
  });

  if (!response.ok) {
    throw new Error(`Search request failed: ${response.status}`);
  }

  return (await response.json()) as SearchResponse;
}

export async function fetchProductDetail(query: URLSearchParams) {
  const response = await fetch(`/api/product?${query.toString()}`, {
    headers: { Accept: "application/json" }
  });

  if (!response.ok) {
    throw new Error(`Product request failed: ${response.status}`);
  }

  return (await response.json()) as ProductDetailResponse;
}

export async function fetchOrderInvoice(query: URLSearchParams) {
  const response = await fetch(`/api/order?${query.toString()}`, {
    headers: { Accept: "application/json" }
  });

  if (!response.ok) {
    throw new Error(`Order request failed: ${response.status}`);
  }

  return (await response.json()) as OrderInvoiceResponse;
}
